import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { FocusableItemTypes } from '.';
import { ToolbarSeparator } from '..';
import { useRefArray } from '../../../../hooks/useRefArray';
import { useIsRTL } from '../../../../locale';
import { renderInlineCssVars, useDesignTokens } from '../../../../theme';
import { setRef } from '../../../../util/ref-util';
import { BadgeButton } from '../../../BadgeButton';
import { Button } from '../../../Button';
import { Checkbox } from '../../../Checkbox';
import { CustomTooltip } from '../../../CustomTooltip';
import { useDensity } from '../../../Density';
import { FileUploader } from '../../../FileUploader';
import { Heading } from '../../../Heading';
import { BaseHelpPopup } from '../../../HelpPopup/__internal__/';
import { Icon } from '../../../Icon';
import { Image } from '../../../Image';
import { Label } from '../../../Label';
import { MenuButton } from '../../../MenuButton';
import { BaseMenuButton } from '../../../MenuButton/__internal__';
import { ObjectSwitcher } from '../../../ObjectManager/ObjectSwitcher';
import { ObjectMarker } from '../../../ObjectMarker';
import { SegmentedButton } from '../../../SegmentedButton';
import { Select } from '../../../Select';
import { SplitButton } from '../../../SplitButton';
import { Text } from '../../../Text';
import { BaseToggleButton } from '../../../ToggleButton/__internal__';
import { UndoRedo } from '../../../UndoRedo';
import { withLabels } from '../../../withLabels';
import { PredefinedButton } from '../PredefinedButton';
import cn from './InnerToolbar.css.json';
import './InnerToolbar.css';
import { jsx as _jsx } from "react/jsx-runtime";
export const SelectWithLabels = withLabels(Select);
export const commonsWithLabelsProps = {
  labelPosition: 'left',
  wrapLabel: false
};
export const renderInnerToolbarItem = (item, focusableIndices, focusedIndex, createItemRef) => {
  const alwaysFocusable = focusableIndices === undefined; // when called from BaseToolbar directly, component should be always focusable
  const focusable = alwaysFocusable ? true : focusableIndices?.some(focusableIndex => focusableIndex === focusedIndex);
  const ref = focusableIndices?.length === 1 ? createItemRef?.(focusableIndices[0]) : undefined; // When focusableIndices.length is greater than 1, handle it separately in the switch statement below
  switch (item.type) {
    case 'Button':
      {
        const {
          type,
          containerStyle,
          containerKey,
          behaviorType,
          customTooltipProps,
          ...rest
        } = item;
        return customTooltipProps ? /*#__PURE__*/_jsx(CustomTooltip, {
          ...customTooltipProps,
          children: /*#__PURE__*/_jsx(Button, {
            ...rest,
            ref: element => {
              ref?.(element);
              if (rest.ref) {
                setRef(rest.ref, element);
              }
            },
            focusable: rest.focusable === undefined ? focusable : rest.focusable && focusable,
            tooltip: rest.tooltip ?? '',
            type: behaviorType
          })
        }) : /*#__PURE__*/_jsx(Button, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: rest.focusable === undefined ? focusable : rest.focusable && focusable,
          type: behaviorType
        });
      }
    case 'PredefinedButton':
      {
        const {
          type,
          buttonType,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(PredefinedButton, {
          ...rest,
          type: buttonType,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'Checkbox':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Checkbox, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          wrapText: false,
          focusable: focusable
        });
      }
    case 'Heading':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Heading, {
          ...rest,
          wrapText: false
        });
      }
    case 'Text':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Text, {
          ...rest,
          tabIndex: focusable ? rest.tabIndex ?? 0 : -1,
          wrapText: false,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          }
        });
      }
    case 'BadgeButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(BadgeButton, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable,
          children: item.icon ? /*#__PURE__*/_jsx(Icon, {
            ...item.icon
          }) : undefined
        });
      }
    case 'MenuButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(MenuButton, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'BaseMenuButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(BaseMenuButton, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'ToolbarSeparator':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ToolbarSeparator, {
          ...rest
        });
      }
    case 'Select':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Select, {
          ...rest,
          selectBoxRef: element => {
            ref?.(element);
            if (rest.selectBoxRef) {
              setRef(rest.selectBoxRef, element);
            }
          },
          focusable: focusable
        });
      }
    case 'SelectWithLabels':
      {
        const {
          type,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SelectWithLabels, {
          ...rest,
          ...commonsWithLabelsProps,
          selectBoxRef: element => {
            ref?.(element);
            if (rest.selectBoxRef) {
              setRef(rest.selectBoxRef, element);
            }
          },
          focusable: focusable
        });
      }
    case 'Label':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Label, {
          ...rest,
          wrapText: false
        });
      }
    case 'Image':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Image, {
          ...rest
        });
      }
    case 'SegmentedButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SegmentedButton, {
          ...rest,
          focusedButtonRef: element => {
            ref?.(element);
            if (rest.focusedButtonRef) {
              setRef(rest.focusedButtonRef, element);
            }
          },
          focusable: focusable,
          enableOverflow: false
        });
      }
    case 'ToggleButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          textToDrop,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(BaseToggleButton, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'FileUploader':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(FileUploader, {
          ...rest,
          ref: element => {
            // FileUploaderRef is not a HTMLElement but it is okay as long as it has `focus` handler implemented
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'HelpPopup':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(BaseHelpPopup, {
          ...rest,
          buttonRef: element => {
            ref?.(element);
            if (rest.buttonRef) {
              setRef(rest.buttonRef, element);
            }
          },
          variant: "toolbar",
          focusable: focusable
        });
      }
    case 'ObjectMarker':
      {
        const {
          type,
          objectMarkerType,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        const isStatic = !item.popperText;
        const canBeFocused = focusableIndices ? focusable : isStatic ? rest.focusable !== false : true;
        return /*#__PURE__*/_jsx(ObjectMarker, {
          ...rest,
          type: objectMarkerType,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: canBeFocused
        });
      }
    case 'SplitButton':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SplitButton, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
    case 'UndoRedo':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        const undoIndex = focusableIndices?.[0];
        const redoIndex = focusableIndices?.[1];
        return /*#__PURE__*/_jsx(UndoRedo, {
          ...rest,
          undoFocusable: alwaysFocusable ? true : undoIndex === focusedIndex,
          redoFocusable: alwaysFocusable ? true : redoIndex === focusedIndex,
          undoBtnRef: element => {
            if (undoIndex !== undefined) {
              createItemRef?.(undoIndex)?.(element);
            }
            if (rest.undoBtnRef) {
              setRef(rest.undoBtnRef, element);
            }
          },
          redoBtnRef: element => {
            if (redoIndex !== undefined) {
              createItemRef?.(redoIndex)?.(element);
            }
            if (rest.redoBtnRef) {
              setRef(rest.redoBtnRef, element);
            }
          }
        });
      }
    case 'ObjectSwitcher':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ObjectSwitcher, {
          ...rest,
          ref: element => {
            ref?.(element);
            if (rest.ref) {
              setRef(rest.ref, element);
            }
          },
          focusable: focusable
        });
      }
  }
};
export const InnerToolbar = props => {
  const isRTL = useIsRTL();
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const innerToolbarRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const {
    focusableIndicesMap,
    focusableItemsCount
  } = getFocusableIndicesInfo(props.items);
  const {
    refs: itemRefs,
    createSetRef: createItemRef
  } = useRefArray();
  const {
    refs: itemContainerRefs,
    createSetRef: createItemContainerRef
  } = useRefArray();

  // Reset focus to the last item when focusedIndex is out of range.
  if (focusedIndex >= focusableItemsCount) setFocusedIndex(focusableItemsCount - 1);
  useEffect(() => {
    if (innerToolbarRef.current?.contains(document.activeElement) && itemRefs[focusedIndex] !== document.activeElement) {
      itemRefs[focusedIndex]?.focus();
    }
  }, [focusedIndex, itemRefs, itemContainerRefs]);
  return /*#__PURE__*/_jsx("div", {
    ref: innerToolbarRef,
    style: renderInlineCssVars({
      xs: density === 'high' ? dt.tokens['space-base-xs'] : dt.tokens['space-base-s']
    }, 'inner-toolbar-spacing'),
    className: cn.innerToolbar,
    "data-testid": props.testId,
    role: "toolbar",
    onKeyDown: e => {
      if (e.isDefaultPrevented()) return;
      switch (e.key) {
        case isRTL ? 'ArrowLeft' : 'ArrowRight':
          e.preventDefault();
          setFocusedIndex(prevFocusedIndex => {
            return Math.min(prevFocusedIndex + 1, focusableItemsCount - 1);
          });
          break;
        case isRTL ? 'ArrowRight' : 'ArrowLeft':
          e.preventDefault();
          setFocusedIndex(prevFocusedIndex => Math.max(prevFocusedIndex - 1, 0));
          break;
        case 'Home':
          setFocusedIndex(0);
          e.preventDefault();
          break;
        case 'End':
          setFocusedIndex(focusableItemsCount - 1);
          e.preventDefault();
          break;
      }
    },
    onFocus: e => {
      // Update focusedIndex
      if (itemContainerRefs[focusedIndex]?.contains(document.activeElement)) return;
      // For components like UndoReo, Undo button and Redo button has the same itemContainer, so use
      // itemRefs first to find the accurate index
      for (const [i, itemRef] of itemRefs.entries()) {
        if (itemRef?.contains?.(e.target)) {
          setFocusedIndex(i);
          return;
        }
      }
      for (const [i, itemContainerRef] of itemContainerRefs.entries()) {
        if (itemContainerRef?.contains(e.target)) {
          setFocusedIndex(i);
          break;
        }
      }
    },
    children: props.items.map((item, i) => {
      const focusableIndices = focusableIndicesMap.get(i) ?? [];
      return /*#__PURE__*/_jsx("div", {
        ref: element => {
          props.createSetRef?.(i)(element);
          // For component that has multiple focusable sub-components, those sub-components share the same itemContainer
          for (const focusableIndex of focusableIndices) {
            createItemContainerRef(focusableIndex)(element);
          }
        },
        style: item.containerStyle,
        className: cx(item.type === 'ToolbarSeparator' && cn.separatorContainer),
        children: renderInnerToolbarItem(item, focusableIndices, focusedIndex, createItemRef)
      }, item.containerKey ?? i);
    })
  });
};
// Some components such as UndoReo have more than one focusable item in it, so map the item's index
// to its focusable indices array
const getFocusableIndicesInfo = items => {
  const focusableIndicesMap = new Map();
  let focusableItemsCount = 0;
  for (const [index, item] of items.entries()) {
    if (!FocusableItemTypes.includes(item.type) || item.type === 'ObjectMarker' && !item.popperText && item.focusable === false) {
      continue;
    }
    if (item.type === 'UndoRedo') {
      focusableIndicesMap.set(index, [focusableItemsCount, focusableItemsCount + 1]);
      focusableItemsCount += 2;
    } else {
      focusableIndicesMap.set(index, [focusableItemsCount]);
      focusableItemsCount++;
    }
  }
  return {
    focusableIndicesMap,
    focusableItemsCount
  };
};
//# sourceMappingURL=InnerToolbar.js.map